'use client';

import { Suspense, useRef, useState } from 'react';
import { ActionableIcon } from '@/app_components/common/ActionableIcon';
import { Icon } from '@/app_components/common/Icon';
import { Link } from '@/app_components/common/Link';
import { Logo } from '@/app_components/common/Logo';
import { SignInButton } from '@/app_components/header/SignInButton';
import { SubscribeButton } from '@/app_components/header/SubscribeButton';
import { FortuneReleaseModal, type FortuneReleaseModalRef } from '@/app_components/release-tools/FortuneReleaseModal';
import { BasicModal } from '@/app_components/ui/BasicModal';
import { ButtonV2 } from '@/app_components/ui/ButtonV2';
import { Close } from '@/app_components/ui/primitives/Dialog';
import { useLongPress } from '@/app_lib/hooks/useLongPress';
import { trackOptimizelyEvent } from '@/app_lib/optimizely/utils';
import { useUser } from '@/app_lib/providers/UserProvider';
import type { UserAccess } from '@/app_lib/types/AuthAccess';
import { ContentPlacement } from '@/app_lib/types/analytics';
import type { NestedMenuItem } from '@/app_lib/types/NestedMenuItem';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

interface ContentProps {
  content: NestedMenuItem[];
  userAccess: UserAccess;
}

const Content = ({ content, userAccess }: ContentProps) => {
  const { loggedIn, hasAccess, isPaused } = userAccess;
  const [activeSubMenu, setActiveSubMenu] = useState<NestedMenuItem | null>(null);

  const hasAccessOrPaused = hasAccess || isPaused;
  return (
    <div
      className='bg-pure h-dvh w-screen md:w-96 flex flex-col pb-6'
      data-cy={ContentPlacement.DrawerMenu}
    >
      <div className='flex items-center gap-6 px-4 border-b border-neutral-300 py-4'>
        <Close
          asChild
          data-cy={ContentPlacement.CloseButton}
        >
          <ActionableIcon>
            <Icon icon='X' />
          </ActionableIcon>
        </Close>
        <Logo className='w-24 ml-[-12px] md:ml-0 items-center justify-center flex' />
        <Close asChild>
          <ActionableIcon
            className='ml-auto'
            data-cy={ContentPlacement.SearchButton}
          >
            {/** TODO: This currently needs to be a label due to how Queryly functions, but we should look into doing this differently */}
            <label
              className='hover:cursor-pointer text-primary-hover'
              htmlFor='queryly_toggle'
            >
              <Icon icon='Search' />
            </label>
          </ActionableIcon>
        </Close>
      </div>
      <div className='overflow-y-scroll scrollbar-hidden pb-20'>
        {activeSubMenu === null ? (
          <ul
            className='flex flex-col gap-1 md:gap-6 bg-pure pl-9 pr-5 py-6 font-semibold text-xl slide-from-left 0.1s'
            data-cy={ContentPlacement.MenuMobile}
          >
            {content.map((item: NestedMenuItem) => (
              <li
                className='flex items-center justify-between min-h-[44px] md:min-h-auto'
                data-cy={`${ContentPlacement.SubmenuItem}0`}
                key={item.id}
              >
                <Link
                  className='hover:text-neutral-600'
                  href={item.url}
                  prefetch={false}
                >
                  {item.label}
                </Link>
                {item.subMenu.length > 0 && (
                  <ActionableIcon
                    data-cy={ContentPlacement.ArrowButton}
                    onClick={() => setActiveSubMenu(item)}
                  >
                    <Icon icon='ChevronRight' />
                  </ActionableIcon>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <ul className='flex flex-col gap-1 md:gap-6 bg-pure pl-9 pr-5 py-6 font-semibold text-xl slide-from-right'>
            <li
              className='flex items-center md:gap-2'
              data-cy={`${ContentPlacement.SubmenuItem}1`}
              key={activeSubMenu.id}
            >
              <ActionableIcon
                className='-ml-2 md:-ml-4 pr-8 md:pr-4'
                data-cy={ContentPlacement.ArrowButton}
                onClick={() => setActiveSubMenu(null)}
              >
                <Icon icon='ChevronLeft' />
              </ActionableIcon>
              <Link
                className='hover:text-neutral-600'
                href={activeSubMenu.url}
                prefetch={false}
              >
                {activeSubMenu.label}
              </Link>
            </li>
            {activeSubMenu.subMenu.map((subItem) => (
              <li
                className='flex items-center pl-12 md:pl-8 min-h-[44px] md:min-h-auto'
                key={subItem.id}
              >
                <Link
                  className='hover:text-neutral-600'
                  href={subItem.url}
                  prefetch={false}
                >
                  {subItem.label}
                </Link>
              </li>
            ))}
          </ul>
        )}
        <div className='absolute md:hidden inset-x-0 bottom-0 px-4 bg-pure py-2 border-t stroke-secondary'>
          {/* If the user is neither logged in nor has access or is paused, show the subscribe button or sign in button */}
          {!(loggedIn && hasAccessOrPaused) && (
            <div className='flex flex-col py-2 gap-2'>
              {!loggedIn && <SignInButton isMobile />}
              <Suspense fallback={<ButtonV2 as='button'>Subscribe for $1</ButtonV2>}>
                {!hasAccessOrPaused && <SubscribeButton noCapitalize>Subscribe for $1</SubscribeButton>}
              </Suspense>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const DrawerMenu = ({ content }: { content: NestedMenuItem[] }) => {
  const { userAccess } = useUser();
  const { optimizely } = useOptimizely();
  const releaseModalRef = useRef<FortuneReleaseModalRef>(null);

  const longPressHandlers = useLongPress({
    duration: 500,
    onLongPress: () => {
      releaseModalRef.current?.openModal();
    },
  });

  const modalContent = (
    <Content
      content={content}
      userAccess={userAccess}
    />
  );

  const trigger = (
    <div
      {...longPressHandlers}
      className='-ml-[13px] md:ml-0'
      id='hamburgerButton'
    >
      <ActionableIcon onClick={() => trackOptimizelyEvent(optimizely, 'navigation_hamburger_menu_click', {})}>
        <Icon
          aria-label='Open Navigation Menu'
          icon='Menu'
        />
      </ActionableIcon>
    </div>
  );

  return (
    <>
      <BasicModal
        content={modalContent}
        description='Navigate through Fortune sections and subsections'
        position='left'
        title='DrawerMenu'
        trigger={trigger}
      />
      <FortuneReleaseModal ref={releaseModalRef} />
    </>
  );
};
